global.$ = global.jQuery = require('jquery');

// Define helper function to get consent manager from parent window
// No need to import or create ConsentManager in iframe - parent page (base.js) owns it
window.getConsentManager = function () {
  // If we're in an iframe, try to get consent manager from parent
  if (window.parent && window.parent !== window) {
    try {
      if (window.parent.__consentManager) {
        console.log('[ConsentManager] Accessed from parent window');
        return window.parent.__consentManager;
      }
    } catch (e) {
      // Cross-origin access blocked
      console.warn('[ConsentManager] Cannot access parent window (cross-origin):', e.message);
    }
  }

  // Fallback for non-iframe contexts (native mode, direct page loads)
  if (window.__consentManager) {
    console.log('[ConsentManager] Using local window instance');
    return window.__consentManager;
  }

  console.log('[ConsentManager] No direct access to consent manager - will use postMessage fallback');
  return null;
};

console.log('[ConsentManager Helper] Defined window.getConsentManager()');

// Initialize ConsentManager for standalone TripBuilder pages (Book Now button scenario)
// This ensures exclusion logic works even when TripBuilder is opened directly
// Skip when in iframe - cross-origin iframes rely on postMessage from parent's ConsentManager
const isInIframe = window.parent !== window;
if (!window.__consentManager && !isInIframe) {
  // Try to get account slug from multiple sources
  let accountSlug = window.accountProps?.account_slug; // From server-rendered accountProps

  // If not in accountProps, extract from domain (e.g., sailsandiego.tripworks.com → sailsandiego)
  if (!accountSlug) {
    const hostname = window.location.hostname;
    // Extract subdomain: sailsandiego.tripworks.com → sailsandiego
    // Handle both dev (demo.dev.tripworks.com) and prod (sailsandiego.tripworks.com)
    const parts = hostname.split('.');
    if (parts.length >= 3) {
      // Remove .dev if present, then take first part
      const firstPart = parts[0];
      // Skip generic subdomains
      if (firstPart !== 'www' && firstPart !== 'tripworks') {
        accountSlug = firstPart;
      }
    }
  }

  if (accountSlug) {
    console.log('[TripBuilder] Standalone page detected - initializing ConsentManager with accountSlug:', accountSlug);

    // Dynamically import and initialize ConsentManager
    import('./ConsentManager.js').then((module) => {
      const ConsentManager = module.default;
      const consentManager = ConsentManager.getInstance();

      // Initialize with account slug
      const operatorManagesConsent = window.accountProps?.operator_manages_consent ?? true;

      consentManager.init({
        operatorManagesConsent,
        accountSlug, // Pass account slug for exclusion check
      });

      console.log('[TripBuilder] ConsentManager initialized successfully');
    });
  }
}

// iOS WebKit messageHandlers defensive wrapper
// Prevent TypeError: "undefined is not an object (evaluating 'window.webkit.messageHandlers[e].postMessage')"
if (typeof window !== 'undefined') {
  if (!window.webkit) {
    window.webkit = {};
  }
  if (!window.webkit.messageHandlers) {
    window.webkit.messageHandlers = {};
  }

  // Only create Proxy if messageHandlers is writable (avoid readonly property error on mobile Chrome)
  try {
    const descriptor = Object.getOwnPropertyDescriptor(window.webkit, 'messageHandlers');
    if (!descriptor || descriptor.writable !== false) {
      // Create a Proxy to handle dynamic property access
      window.webkit.messageHandlers = new Proxy(window.webkit.messageHandlers, {
        get: function (target, property) {
          if (target[property]) {
            return target[property];
          }
          // Return a mock handler with postMessage method for any undefined handler
          return {
            postMessage: function () {
              return Promise.resolve(null);
            },
          };
        },
      });
    }
  } catch (e) {
    // If Proxy assignment fails (readonly property), we'll use the existing messageHandlers
    console.warn('WebKit messageHandlers proxy creation failed:', e.message);
  }
}

require('../../../../public/css/core.css');
require('../../../../public/css/theme-main.css');
require('../../../../public/css/tripbuilder.css');
require('../../../../public/css/pos.css');
require('../../../../public/css/trip.css');
require('../../../../public/css/trip-builder-widget.css');

Promise.all([
  import('./TripBuilderInterface.vue'),
  import('vue'),
  import('vue-moment'),
  import('moment-timezone'),
  import('bootstrap-vue'),
  import('./../shared/VueFilters'),
  import('@fortawesome/fontawesome-pro/css/all.min.css'),
  import('pretty-checkbox/dist/pretty-checkbox.min.css'),
  import('@bugsnag/js'),
  import('@bugsnag/plugin-vue'),
  import('vue-router'),
  import('../../routes'),
  import('select2'),
  import('@vue/composition-api'),
  import('../../directives/translatable'),
  import('../../i18n.js'),
  import('pinia'),
  import('../../mixpanel.js'),
  import('../../plugins/ui-library'),
  import('select2/dist/css/select2.css'),
  import('vue-select/dist/vue-select.css'),
  import('vue-multiselect/dist/vue-multiselect.min.css'),
  import('bootstrap-vue/dist/bootstrap-vue.css'),
  import('bootstrap'),
  require('bootstrap-notify'),
  require('ag-grid-community/styles/ag-grid.css'),
  require('ag-grid-community/styles/ag-theme-quartz.css'),
])
  .then((modules) => {
    const TripBuilderInterface = modules[0].default;
    const Vue = modules[1].default;
    const vueMoment = modules[2].default;

    const moment = modules[3].default;

    const BootstrapVue = modules[4];

    const Bugsnag = modules[8].default;
    const BugsnagPluginVue = modules[9].default;

    const vueRouter = modules[10].default;
    const vueRoutes = modules[11].default;
    const select2 = modules[12].default;
    select2($);
    const compositionApi = modules[13];
    const translation = modules[14];
    const i18nInstance = modules[15].default;
    const { createPinia, PiniaVuePlugin } = modules[16];
    const mixpanel = modules[17].default;
    const uiLibrary = modules[18].default;

    Vue.use(translation);
    Vue.use(mixpanel);
    Vue.use(uiLibrary);
    Vue.use(BootstrapVue);
    Vue.use(vueMoment, { moment });
    Vue.use(vueRouter);

    Vue.use(PiniaVuePlugin);
    const pinia = createPinia();

    const router = new vueRouter({
      mode: 'history',
      routes: vueRoutes,
    });
    Vue.use(compositionApi);

    if (process.env.NODE_ENV !== 'development') {
      if (process.env.BUGSNAG_KEY) {
        Bugsnag.start({
          apiKey: process.env.BUGSNAG_KEY,
          plugins: [new BugsnagPluginVue()],
          releaseStage: process.env.APP_ENV == 'prod' ? 'production' : process.env.APP_ENV,
          enabledReleaseStages: ['production', 'stage'],
          appVersion: process.env.DEPLOY_COMMIT || 'unknown',
        });

        // Add deployment metadata
        Bugsnag.addMetadata('deployment', {
          branch: process.env.DEPLOY_BRANCH || 'unknown',
          commit: process.env.DEPLOY_COMMIT || 'unknown',
          timestamp: process.env.DEPLOY_TIMESTAMP || '',
          environment: process.env.APP_ENV || 'unknown',
        });
        Bugsnag.addMetadata('URL', location);
        Bugsnag.getPlugin('vue').installVueErrorHandler(Vue);
        Vue.use(Bugsnag);
      }
    }

    window.vueTripBuilder = new Vue({
      i18n: i18nInstance,
      el: '#vue-trip-builder',
      router,
      components: {
        TripBuilderInterface,
      },
      pinia,
      mounted: function () {
        // Check if we're in native mode (not in iframe) - skip all postMessage calls
        const isNative = document.body.classList.contains('native');
        if (isNative) {
          console.log('[mounted] Native mode detected - skipping all postMessage calls');
          return;
        }

        this.postMessageToParent('modalIframeIsLoaded');

        // Use nextTick to wait for DOM to fully render
        this.$nextTick(() => {
          this.postDimensionsToParent();

          // Also retry after a short delay to catch any async content
          setTimeout(() => {
            this.postDimensionsToParent();
          }, 100);

          setTimeout(() => {
            this.postDimensionsToParent();
          }, 500);
        });

        // Set up ResizeObserver to detect content height changes (for embedded widgets)
        const isEmbed = this.getUrlParameter('isEmbed');
        if (isEmbed === 'true') {
          const vueTripBuilder = document.getElementById('vue-trip-builder');
          if (vueTripBuilder && typeof ResizeObserver !== 'undefined') {
            const resizeObserver = new ResizeObserver(() => {
              this.postDimensionsToParent();
            });
            resizeObserver.observe(vueTripBuilder);
          }
        }
      },
      methods: {
        getUrlParameter: function (name) {
          name = name.replace(/[\[]/, '\\[').replace(/[\]]/, '\\]');
          const regex = new RegExp('[\\?&]' + name + '=([^&#]*)');
          const results = regex.exec(location.search);
          return results === null ? '' : decodeURIComponent(results[1].replace(/\+/g, ' '));
        },
        postDimensionsToParent: function (action) {
          // Don't send resize messages when in native mode (not in iframe)
          const isNative = document.body.classList.contains('native');
          if (isNative) {
            return; // Silent skip - already logged in mounted()
          }

          // Don't send resize messages when in a modal
          const inModal = this.getUrlParameter('inModal');
          if (inModal === 'true') {
            console.log('[postDimensionsToParent] Skipping - widget is in modal, not an embed');
            return;
          }

          const vueTripBuilder = document.getElementById('vue-trip-builder');
          let height = vueTripBuilder ? vueTripBuilder.scrollHeight : 0;

          if (document.getElementsByClassName('sf-toolbar').length > 0) {
            const toolbarHeight = document.getElementsByClassName('sf-toolbar')[0].scrollHeight;
            height += toolbarHeight;
          }

          if (
            typeof this.lastReportedHeight == 'undefined' ||
            height > this.lastReportedHeight ||
            height < this.lastReportedHeight
          ) {
            parent.postMessage(
              JSON.stringify({
                action: 'updateTripOrderDimensions',
                height: height,
                tripBuilderId: this.getUrlParameter('tripBuilderId'),
              }),
              '*',
            );
            this.lastReportedHeight = height;
          }
        },
        postMessageToParent: function (action) {
          // Don't send messages when in native mode (not in iframe)
          const isNative = document.body.classList.contains('native');
          if (isNative) {
            return; // Silent skip - already logged in mounted()
          }

          parent.postMessage(JSON.stringify({ action: action }), '*');
        },
        closeModal: function () {
          this.postMessageToParent('closeTripBuilderModal');
        },
      },
    });

    // ... rest of your code ...
  })
  .catch((error) => {
    console.error(error.message);
  });
